<template>
  <simple-scrollable-modal class="new-item-container">
    <template #title>
      <h4>{{ title }}</h4>
    </template>
    <template #content>
      <template v-if="!nItem">
        <v-skeleton-loader type="paragraph" />
      </template>
      <template v-else>
        <v-tabs v-model="tabIndex">
          <v-tab>Básico</v-tab>
          <v-tab>Opciones</v-tab>
          <v-tab>Impresión</v-tab>
          <v-tab>Traducciones <RestaurantTierChip :tier="1" class="ml-1" /></v-tab>
          <v-tab v-if="canManageStock">Stock</v-tab>
          <v-tab v-if="hasBranches">Sucursales</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tabIndex">
          <v-tab-item eager>
            <v-form @submit.prevent v-model="valid" ref="form">
              <single-image-uploader
                v-model="nItem.picture"
              ></single-image-uploader>
              <v-select
                :label="$t('category')"
                class=""
                hide-details
                :items="categories"
                v-model="nItem.categoryId"
                item-text="name"
                item-value="id"
                outlined
                dense
              ></v-select>
              <v-select
                v-if="reportingCategories.length"
                class="mt-3"
                :label="$t('reportingCategories.itemFieldLabel')"
                persistent-hint
                :hint="$t('reportingCategories.itemFieldHint')"
                :items="reportingCategoryOptions"
                v-model="nItem.reportingCategoryId"
                item-text="text"
                item-value="value"
                outlined
                dense
              ></v-select>
              <v-row>
                <v-col cols="12" md="12">
                  <v-text-field
                    :label="$t('name')"
                    v-model="nItem.name"
                    :rules="$rule('required|min3|max128')"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <!--<v-col cols="12" md="4">
                  <v-text-field :label="$t('identifier')" class="mb-0"
                                v-model="nItem.stringId"
                                :persistent-hint="true"
                                :hint="$t('identifier.formHint')" outlined dense ></v-text-field>
                </v-col>-->
              </v-row>

              <div
                class="price-container"
                :class="{
                  'price-container--with-variants':
                    !!priceVariants && !priceVariants.deletedAt,
                }"
              >
                <v-row align="center" justify="center" no-gutters class="mb-4">
                  <v-col cols="auto" class="children-inline">
                    <span style="font-size: 16px">Precio único</span>
                    <v-switch
                      class="mt-0 pt-0"
                      :input-value="!!priceVariants && !priceVariants.deletedAt"
                      @change="togglePriceVariants"
                      hide-details
                      label="Precios múltiples"
                    ></v-switch>
                  </v-col>
                </v-row>

                <template v-if="!priceVariants || !!priceVariants.deletedAt">
                  <v-row justify="center">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        label="Precio"
                        v-model="nItem.price"
                        hide-details
                        :rules="$rule('required|number')"
                        type="number"
                        outlined
                        dense
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <div>
                        <v-checkbox
                          :label="$t('item.hasDiscount')"
                          hide-details
                          class="ma-0"
                          :input-value="nItem.discountedPrice !== null"
                          @change="toggleDiscount"
                        />
                      </div>
                      <v-row class="">
                        <v-col cols="12" class="pb-0">
                          <v-text-field
                            :label="$t('item.discountedPrice')"
                            class="mt-2"
                            type="number"
                            v-if="nItem.discountedPrice !== null"
                            v-model="nItem.discountedPrice"
                            persistent-hint
                            :hint="`
                                          ${
                                            nItem.discountedPrice <= nItem.price
                                              ? '-'
                                              : '+'
                                          }${currency.symbol}${Math.abs(
                              nItem.price - nItem.discountedPrice
                            )}
                                          (${
                                            nItem.discountedPrice <= nItem.price
                                              ? '-'
                                              : '+'
                                          }%${round(
                              Math.abs(
                                100 * (1 - nItem.discountedPrice / nItem.price)
                              )
                            )})
                                        `"
                            :rules="[
                              ...$rule('required|number'),
                              (v) =>
                                parseInt(v) < parseInt(nItem.price) ||
                                $t('item.errorDiscountedPrice'),
                            ]"
                            outlined
                            dense
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <V2ItemVariantsSection
                    :price-variants="priceVariants"
                    :currency="currency"
                    @defaultCostChanged="syncDefaultPrice"
                    @defaultDiscountedCostChanged="syncDefaultDiscountedPrice"
                  />
                </template>
              </div>

              <div class="tw-w-1/2">
                <v-text-field
                  label="IVA / VAT (%)"
                  v-model="nItem.vat"
                  type="number"
                  outlined
                  dense
                  class="mt-4"
                  hide-details
                  prefix="%"
                  :rules="$rule('required|number')"
                ></v-text-field>
              </div>

              <v-textarea
                :label="$t('description')"
                v-model="nItem.description"
                class="mt-8"
                outlined
                dense
                hide-details
              ></v-textarea>

              <div class="mt-4 px-3">
                <div>
                  <span>{{ $t('allergens') }}</span>
                  <v-btn icon @click="chooseAllergens" small
                    ><v-icon>edit</v-icon></v-btn
                  >
                </div>
                <div class="tw-flex tw-items-center">
                  <img
                    v-for="allergen of allergens"
                    v-if="(allergen.id & nItem.allergens) === allergen.id"
                    class="allergen-img mr-1"
                    :src="`${uploadsPath}/static/alergenos/${allergen.picture}.png`"
                  />
                </div>
              </div>

              <div class="mt-4">
                <!-- Plain button to add recipe, that sets recipe to '' -->
                <v-btn
                  v-if="nItem.recipe === null"
                  color="primary"
                  text
                  @click="nItem.recipe = ''"
                  >Agregar receta</v-btn
                >
                <v-textarea
                  v-if="nItem.recipe !== null"
                  label="Receta"
                  placeholder="Opcional"
                  v-model="nItem.recipe"
                  outlined
                  dense
                  hide-details
                ></v-textarea>
              </div>
            </v-form @submit.prevent>
          </v-tab-item>
          <v-tab-item eager>
            <v-skeleton-loader v-if="!itemOptions" type="paragraph" />
            <V2ItemOptionsSection
              v-else
              :item-options="itemOptions"
              :item="nItem"
              :restaurant="restaurant"
              :branch="branch"
              :menu-id="menuId"
            />
          </v-tab-item>
          <v-tab-item class="mt-4">
            <div>
              <v-switch
                :input-value="customizeComandaPrinters"
                @change="toggleCustomComandaPrinters"
                label="Personalizar impresoras de comanda para este producto"
                dense
                hide-details
              ></v-switch>
              <template v-if="!customizeComandaPrinters">
                <p class="grey--text mt-2 mb-0">
                  Este producto se imprime en las comanderas de su categoría:
                  <strong>{{ inheritedStationNames }}</strong>
                </p>
              </template>
              <template v-else>
                <v-checkbox
                  v-for="station of printStations"
                  :key="station.stationId"
                  v-model="nItem.comandaPrinters"
                  :label="`Comandera ${station.name}`"
                  :value="station.stationId"
                  hide-details
                  dense
                ></v-checkbox>
                <p
                  v-if="!nItem.comandaPrinters || !nItem.comandaPrinters.length"
                  class="orange--text mt-3 mb-0"
                >
                  Este producto no se imprimirá en ninguna comanda
                </p>
              </template>
              <v-divider class="my-4" />
              <v-switch
                v-model="comandaSeparate"
                :label="$t('item.comandaSeparate')"
                dense
                hide-details
              ></v-switch>
              <v-switch
                v-if="comandaSeparate"
                v-model="comandaGroupIdentical"
                :label="$t('item.comandaGroupIdentical')"
                class="ml-6"
                dense
                hide-details
              ></v-switch>
              <V2ItemComandaSplitPreview
                class="mt-4"
                :mode="nItem.comandaSplit"
                :item-name="nItem.name"
                :muted="
                  customizeComandaPrinters &&
                  (!nItem.comandaPrinters || !nItem.comandaPrinters.length)
                "
              />
            </div>
          </v-tab-item>
          <v-tab-item class="mt-4">
            <template v-if="!restaurantLanguages">
              <p class="grey--text">
                Si quiere traducir su menú a otros idiomas, asegúrese de elegir
                los idiomas en Menú → Ajustes del menú.
              </p>
            </template>
            <template v-else-if="!translationsByLanguage">
              <v-skeleton-loader type="paragraph" />
            </template>
            <template v-else>
              <v-chip
                :color="translationsStatus.color"
                :dark="translationsStatus.dark"
                class="mb-2"
                small
              >
                <v-icon class="mr-1" small
                :class="translationsStatus.iconClass">{{ translationsStatus.icon }}</v-icon>
                <span>{{ translationsStatus.text }}</span>
              </v-chip>
              <p class="grey--text">
                Los idiomas que no tengan traducción se mostrarán en el idioma
                original.<br/>
                <strong>La traducción automática puede tardar hasta una hora.</strong>
              </p>
              <v-form @submit.prevent v-model="translationsValid">
                <div class="translations">
                  <CategoryTranslation
                    v-for="lang of restaurantLanguages"
                    :key="lang.id"
                    :language="lang"
                    @toggle="toggleTranslations(lang.id)"
                    :translation="translationsByLanguage[lang.id] || null"
                    @change="translationsChanged = true"
                  />
                </div>
              </v-form @submit.prevent>
              <div
                v-if="
                  variantTranslations &&
                  (persistedVariants.length || hasUnsavedVariants)
                "
                class="variant-translations mt-6"
              >
                <h4 class="mb-2">{{ $t('itemTranslations.variantsTitle') }}</h4>
                <p
                  v-if="hasUnsavedVariants"
                  class="variant-translations-hint grey--text text-body-2 mb-2"
                >
                  {{ $t('itemTranslations.unsavedVariantsHint') }}
                </p>
                <div
                  v-for="lang of restaurantLanguages"
                  :key="'vt-' + lang.id"
                  class="variant-translations-language mb-3"
                >
                  <span class="variant-translations-language-name">{{
                    lang.name
                  }}</span>
                  <V2TranslationEntryRow
                    v-for="variant of persistedVariants"
                    :key="lang.id + '-' + variant.id"
                    class="mt-1"
                    :language="lang"
                    :label="variant.title"
                    :value="variantTranslations[lang.id + '|' + variant.id]"
                    @input="
                      $set(
                        variantTranslations,
                        lang.id + '|' + variant.id,
                        $event
                      )
                    "
                  />
                </div>
              </div>
            </template>
          </v-tab-item>
          <v-tab-item v-if="canManageStock">
            <ModalsItemStock 
              ref="childStockRecipes"
              :item="nItem"
              v-model="stockRecipes"
            />
          </v-tab-item>
          <v-tab-item class="mt-4" v-if="hasBranches">
            <p class="grey--text">
              Si quiere que este item no se muestre en algunas sucursales,
              desactívelas debajo.
            </p>
            <div class="branches">
              <v-switch
                v-for="branch of activeBranches"
                :key="branch.id"
                :input-value="branchSelected(branch)"
                @change="toggleBranch(branch)"
                hide-details
                :label="branch.name"
                dense
                v-if="hasPermissionInBranch(branch.id)"
              />
            </div>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </template>
    <template #action-bar>
      <div class="d-flex flex-row align-center justify-end">
        <v-btn text v-if="item.id" @click="remove" color="red"
          ><v-icon>mdi-delete</v-icon> {{ $t('delete') }}</v-btn
        >
        <v-btn text @click="cancel">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" @click="save" :disabled="loading">{{
          $t('save')
        }}</v-btn>
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { IItem } from '../../interfaces/IItem';
  import ItemsService from '../../api/services/items.service';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { ICategory } from '../../interfaces/ICategory';
  import RestaurantsService from '../../api/services/restaurants.service';
  import AllergensModal from './AllergensModal.vue';
  import { allergens } from '../../enums/Allergens';
  import SingleImageUploader from '../SingleImageUploader.vue';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { IOption } from '../../interfaces/IOption';
  import { OptionType } from '../../enums/OptionType';
  import { IOptionItem } from '../../interfaces/IOptionItem';
  import { roundPrice } from '../../utils';
  import ConfirmModal from './ConfirmModal.vue';
  import { ILanguage } from '../../interfaces/ILanguage';
  import TranslationsService, {
    IOptionTranslationEntry,
    ITranslationData,
  } from '../../api/services/translations.service';
  import CategoryTranslation from './CategoryTranslation.vue';
  import OptionsService, {
    ICreateOptionDto,
  } from '../../api/services/options.service';
  import V2ItemOptionsSection from './item/options/V2ItemOptionsSection.vue';
  import { IItemOptionRow } from './item/options/V2ItemOptionRow.vue';
  import V2ItemVariantsSection from './item/variants/V2ItemVariantsSection.vue';
  import V2ItemComandaSplitPreview from './item/printing/V2ItemComandaSplitPreview.vue';
  import V2TranslationEntryRow, {
    ITranslationEntryModel,
  } from './translations/V2TranslationEntryRow.vue';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import { IBranch } from '../../interfaces/IBranch';
  import SimpleScrollableModal from './base/SimpleScrollableModal.vue';
import { Globals } from '@/modules/Globals';
import { useRestaurantStore } from '@/stores/restaurant.store';
import RestaurantTierChip from '../partials/RestaurantTierChip.vue';
import ModalsItemStock from './item/ModalsItemStock.vue';
import { IStockRecipe } from '@/interfaces/IStockRecipe';
import { CustomError } from '@/exceptions/custom.exception';
import ReportingCategoriesService from '@/api/services/reporting-categories.service';
import { IReportingCategory } from '@/interfaces/IReportingCategory';
import { PermissionType } from '@/enums/PermissionType';
import { ComandaSplit, isComandaSplit } from '@/enums/ComandaSplit';

  @Component({
    components: {
      SimpleScrollableModal,
      CategoryTranslation,
      SingleImageUploader,
      RestaurantTierChip,
      ModalsItemStock,
      V2ItemOptionsSection,
      V2ItemVariantsSection,
      V2TranslationEntryRow,
      V2ItemComandaSplitPreview,
    },
  })
  export default class NewItemModal extends Vue {
    $refs: {
      form: any;
      childStockRecipes: any;
    };
    @Prop() menuId: number | null;
    @Prop() restaurant: IRestaurant;
    @Prop() branch: IBranch;
    @Prop() branches: IBranch[];
    @Prop() item: IItem;
    @Prop() currency: ICurrency;
    @Prop() category: ICategory;
    nItem: IItem = null; // Acá copio el item que me mandan para no modificarlo directamente
    itemOptions: IItemOptionRow[] = null;
    loading = false;
    tabIndex = 0;
    itemsService = new ItemsService();
    translationsService = new TranslationsService();
    title = '';
    categories: ICategory[] = [];
    reportingCategories: IReportingCategory[] = [];
    restaurantsService = new RestaurantsService();
    allergens = allergens;
    restaurantLanguages: ILanguage[] = null;
    translationsByLanguage: { [languageId: string]: ITranslationData } = null;
    valid = false;
    translationsValid = false;
    translationsChanged = false;
    variantTranslations: {
      [key: string]: ITranslationEntryModel;
    } = null;
    variantTranslationsOriginal: {
      [key: string]: ITranslationEntryModel;
    } = {};
    stockRecipes: IStockRecipe[] | null = null;
    restaurantStore = useRestaurantStore();
    customizeComandaPrinters = false;

    /** Switch 1: separate documents (unit or line mode) vs. together with the order. */
    get comandaSeparate(): boolean {
      return this.nItem.comandaSplit !== ComandaSplit.None;
    }
    set comandaSeparate(value: boolean) {
      this.nItem.comandaSplit = value ? ComandaSplit.Unit : ComandaSplit.None;
    }

    /** Switch 2 (only while separate): group identical units on one document. */
    get comandaGroupIdentical(): boolean {
      return this.nItem.comandaSplit === ComandaSplit.Line;
    }
    set comandaGroupIdentical(value: boolean) {
      this.nItem.comandaSplit = value ? ComandaSplit.Line : ComandaSplit.Unit;
    }

    get printStations() {
      return this.restaurantStore.printStations;
    }

    get inheritedStationNames() {
      const category = this.categories.find(
        (c) => c.id === this.nItem?.categoryId
      );

      const stationIds = category?.comandaPrinters ?? [];
      if (!stationIds.length) return 'ninguna';

      return stationIds
        .map((id) => this.restaurantStore.stationName(id))
        .join(', ');
    }

    toggleCustomComandaPrinters(enabled: boolean) {
      this.customizeComandaPrinters = !!enabled;

      if (this.customizeComandaPrinters) {
        // Start from the category's stations so enabling doesn't change
        // behavior until the user unchecks something.
        const category = this.categories.find(
          (c) => c.id === this.nItem.categoryId
        );
        Vue.set(
          this.nItem,
          'comandaPrinters',
          [...(category?.comandaPrinters ?? [])]
        );
      } else {
        Vue.set(this.nItem, 'comandaPrinters', null);
      }
    }

    mounted() {
      this.title = !this.item.id
        ? this.$tc('item.new')
        : this.$t('item.editing', { name: this.item.name }).toString();

      this.restaurantLanguages = vueData.restaurantLanguages;

      this.loadCategories();
      this.loadReportingCategories();

      this.init();
    }

    @Watch('tabIndex')
    async onTabChanged(newIndex) {
      // Traducciones tab (index 3, after Básico/Opciones/Impresión)
      if (newIndex === 3 && !this.translationsByLanguage) {
        await this.loadTranslations();
      }
    }

    get activeBranches() {
      return vueData.branches.filter((b) => b.active);
    }

    get hasBranches() {
      return vueData.allBranches && this.activeBranches.length > 1;
    }

    /** GET /stock/recipes requires CatalogManage; hide the tab otherwise. */
    get canManageStock() {
      return vueData.hasPermission(PermissionType.CatalogManage);
    }

    branchSelected(branch: IBranch) {
      const id = branch.id + '';
      return (
        !this.nItem.hiddenBranchesIds ||
        this.nItem.hiddenBranchesIds.indexOf(id) === -1
      );
    }

    toggleBranch(branch: IBranch) {
      const id = branch.id + '';
      if (!this.nItem.hiddenBranchesIds) this.nItem.hiddenBranchesIds = [id];
      else {
        const index = this.nItem.hiddenBranchesIds.indexOf(id);
        if (index > -1) {
          this.nItem.hiddenBranchesIds.splice(index, 1);
          if (this.nItem.hiddenBranchesIds.length === 0)
            this.nItem.hiddenBranchesIds = null;
        } else this.nItem.hiddenBranchesIds.push(id);
      }
    }

    hasPermissionInBranch(branchId: number) {
      return (
        vueData.role.allBranches ||
        vueData.role.branches.some((b) => b.id === branchId)
      );
    }

    async loadTranslations() {
      this.translationsByLanguage = null;

      if (!this.item.id) {
        this.translationsByLanguage = {};
        this.variantTranslations = {};
        return;
      }

      try {
        const translations = await this.translationsService.getAllByItem(
          this.item.restaurantId,
          this.item.id
        );

        const translationsByLanguage = {};

        for (const t of translations) {
          let translation = translationsByLanguage[t.languageId];
          if (!translation) {
            translation = translationsByLanguage[t.languageId] = {
              id: t.key,
              languageId: t.languageId,
              outdated: t.outdated,
            };
          }

          const key = t.key.split('-')[1];
          switch (key) {
            case 'name':
              translation.name = t.value;
              break;
            case 'desc':
              translation.description = t.value;
              break;
          }
        }

        this.translationsByLanguage = translationsByLanguage;

        await this.loadVariantTranslations();
      } catch (e) {
        // Degrade to empty editors instead of a stuck skeleton (e.g. roles
        // without the translations permission).
        console.error(e);
        this.translationsByLanguage = this.translationsByLanguage ?? {};
        this.variantTranslations = this.variantTranslations ?? {};
        vueData.serverError(e);
      }
    }

    get persistedVariants(): IOptionItem[] {
      if (!this.priceVariants?.id || this.priceVariants.deletedAt) return [];
      return (this.priceVariants.optionItems ?? []).filter(
        (v) => v.id && !v.deletedAt
      );
    }

    get hasUnsavedVariants(): boolean {
      if (!this.priceVariants || this.priceVariants.deletedAt) return false;
      return (this.priceVariants.optionItems ?? []).some(
        (v) => !v.id && !v.deletedAt
      );
    }

    async loadVariantTranslations() {
      this.variantTranslations = null;

      const variants = this.persistedVariants;
      if (!variants.length || !this.restaurantLanguages?.length) {
        this.variantTranslations = {};
        return;
      }

      const rows = await this.translationsService.getOptionTranslations(
        this.item.restaurantId,
        this.priceVariants.id
      );

      // Build every (variant, language) model upfront so bindings stay reactive.
      const map: { [key: string]: ITranslationEntryModel } = {};
      for (const lang of this.restaurantLanguages) {
        for (const variant of variants) {
          map[`${lang.id}|${variant.id}`] = {
            title: null,
            description: null,
            outdated: false,
          };
        }
      }

      for (const row of rows) {
        const match = /^oi(\d+)-(title|desc)$/.exec(row.key);
        if (!match) continue;

        const model = map[`${row.languageId}|${match[1]}`];
        if (!model) continue;

        if (match[2] === 'title') model.title = row.value;
        else model.description = row.value;
        if (row.outdated) model.outdated = true;
      }

      this.variantTranslationsOriginal = JSON.parse(JSON.stringify(map));
      this.variantTranslations = map;
    }

    async saveVariantTranslations() {
      if (!this.variantTranslations) return;

      const variants = this.persistedVariants;
      if (!variants.length || !this.restaurantLanguages?.length) return;

      const normalize = (value: string | null | undefined) =>
        value?.trim() ? value.trim() : null;

      const changed: IOptionTranslationEntry[] = [];
      for (const lang of this.restaurantLanguages) {
        for (const variant of variants) {
          const key = `${lang.id}|${variant.id}`;
          const current = this.variantTranslations[key];
          const original = this.variantTranslationsOriginal[key];
          if (!current) continue;

          const title = normalize(current.title);
          const description = normalize(current.description);
          if (
            title === normalize(original?.title) &&
            description === normalize(original?.description)
          ) {
            continue;
          }

          changed.push({
            optionItemId: variant.id,
            languageId: lang.id,
            title,
            description,
          });
        }
      }

      if (changed.length === 0) return;

      await this.translationsService.saveOptionTranslations(
        this.item.restaurantId,
        this.priceVariants.id,
        changed
      );
      this.variantTranslationsOriginal = JSON.parse(
        JSON.stringify(this.variantTranslations)
      );
    }

    get translationsStatus() {
      const status = {
        icon: 'mdi-refresh',
        text: 'Cargando...',
        iconClass: 'tw-animate-spin',
        color: 'grey',
        dark: false,
      };

      if (!this.translationsByLanguage) return status

      if (!this.item.visible) {
        status.text = 'Este item no está visible';
        status.icon = 'mdi-eye-off';
        status.iconClass = '';
        status.color = 'grey';
        status.dark = true;
        return status;
      }

      const restaurantLanguages = vueData.restaurant.languageIds ?? [];

      const allLanguagesUptodate = restaurantLanguages.every(
        (langId) => {
          const translations = this.translationsByLanguage[langId];

          if (!translations || translations.outdated) return false;

          if (this.item.description && !translations.description) return false;

          return true;
        }
      );

      if (!allLanguagesUptodate) {
        status.text = 'Traduciendo cambios...';
        status.color = 'orange';
        status.dark = true;
      } else {
        status.text = 'Traducciones listas';
        status.color = 'green';
        status.dark = true;
        status.icon = 'mdi-check';
        status.iconClass = '';
      }

      return status;
    }

    toggleTranslations(langId: string) {
      if (!this.translationsByLanguage[langId]) {
        Vue.set(this.translationsByLanguage, langId, {
          languageId: langId,
          name: null,
          description: null,
        });
        this.translationsChanged = true;
      } else {
        Vue.delete(this.translationsByLanguage, langId);
        this.translationsChanged = true;
      }
    }

    async init() {
      if (!this.item.id) {
        this.nItem = { ...this.item };
        this.itemOptions = [];
      } else {
        this.nItem = await this.itemsService.getById(this.item.id);
        this.itemOptions = await this.itemsService.getOptions(this.item.id);
        // Ensure child components (e.g. Stock tab) can rely on `item.itemOptions`
        // even though `/items/:id` no longer returns non-variant options in `item.options`.
        Vue.set(this.nItem, 'itemOptions', this.itemOptions);
      }

      // Reactive-safe default: v-model assigns plainly, so the key must exist.
      if (this.nItem.reportingCategoryId === undefined) {
        Vue.set(this.nItem, 'reportingCategoryId', null);
      }
      // New items ({ ...this.item }) have no key; the switches' accessors need
      // a reactive one (an unknown value also falls back to the default).
      if (!isComandaSplit(this.nItem.comandaSplit)) {
        Vue.set(this.nItem, 'comandaSplit', ComandaSplit.None);
      }

      this.customizeComandaPrinters = Array.isArray(this.nItem.comandaPrinters);
    }

    chooseAllergens() {
      this.$modal.show(
        AllergensModal,
        {
          item: this.nItem,
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: (allergens) => {
            this.nItem.allergens = allergens;
          },
        }
      );
    }

    get priceVariants() {
      return this.nItem.priceVariants;
    }

    set priceVariants(option: IOption | null) {
      Vue.set(this.nItem, 'priceVariants', option);
    }

    get priceVariantItems() {
      return this.priceVariants?.optionItems.filter(
        (value) => !value.deletedAt
      );
    }

    get activePriceVariantsCount() {
      return (
        this.priceVariants &&
        this.priceVariantItems.filter((value) => value.active).length
      );
    }

    get uploadsPath() {
      return Globals.uploadsPath;
    }

    toggleDiscount() {
      if (this.nItem.discountedPrice === null) {
        this.nItem.discountedPrice = Math.round(this.nItem.price * 0.9);
      } else {
        this.nItem.discountedPrice = null;
      }
    }

    togglePriceVariants() {
      if (this.priceVariants && !this.priceVariants.deletedAt) {
        if (this.priceVariants.id) {
          this.priceVariants.deletedAt = new Date();
        } else {
          this.priceVariants = null;
        }
      } else {
        if (this.priceVariants) {
          this.priceVariants.deletedAt = null;
          this.priceVariants.optionItems[0].discountedCost = this.nItem.discountedPrice;
        } else {
          this.priceVariants = {
            main: true,
            mandatory: true,
            multiple: false,
            min: 0,
            max: 0,
            optionItems: [],
            active: true,
            type: OptionType.Choose,
            title: 'Elige el tamaño',
          } as IOption;

          for (let i = 0; i < 2; i++) {
            this.addPriceVariant(i === 0);
          }
          this.priceVariants.optionItems[0].discountedCost = this.nItem.discountedPrice;
        }
      }
    }

    addPriceVariant(basic: boolean = false) {
      const item: IOptionItem = <IOptionItem>{
        title: '',
        description: null,
        picture: null,
        cost: basic ? this.nItem.price : null,
        discountedCost: null,
        active: true,
      };

      this.priceVariants.optionItems.push(item);
    }

    syncDefaultPrice() {
      const firstVariant = this.priceVariantItems[0];
      if (firstVariant) this.nItem.price = firstVariant.cost;
    }

    syncDefaultDiscountedPrice() {
      const firstVariant = this.priceVariantItems[0];
      if (firstVariant) this.nItem.discountedPrice = firstVariant.discountedCost;
    }

    /** Whitelists the fields POST /options accepts out of a draft snapshot. */
    private toCreateOptionDto(option: IOption): ICreateOptionDto {
      return {
        type: option.type,
        title: option.title,
        description: option.description ?? null,
        main: option.main,
        mandatory: option.mandatory,
        multiple: option.multiple,
        min: option.min ?? null,
        max: option.max ?? null,
        optionItems: (option.optionItems ?? [])
          .filter((oI) => !oI.deletedAt)
          .map((oI, idx) => ({
            title: oI.title,
            description: oI.description ?? null,
            picture: oI.picture ?? null,
            cost: oI.cost,
            discountedCost: oI.discountedCost ?? null,
            active: oI.active,
            index: oI.index ?? idx,
          })),
      };
    }

    private validateVariants(): boolean {
      if (!this.priceVariants || this.priceVariants.deletedAt) return true;

      if (this.activePriceVariantsCount === 0) {
        vueData.addAlert(
          'Al menos un precio debe estar activo',
          AlertType.Error
        );
        return false;
      }

      const invalid = this.priceVariantItems.some((v) => {
        const rawCost: number | string | null | undefined = v.cost;
        return (
          !v.title?.trim() ||
          rawCost === null ||
          rawCost === undefined ||
          rawCost === '' ||
          isNaN(Number(rawCost))
        );
      });
      if (invalid || !this.priceVariants.title?.trim()) {
        vueData.addAlert(
          this.$tc('itemVariants.errorIncomplete'),
          AlertType.Error
        );
        return false;
      }

      return true;
    }

    async save() {
      this.$refs.form.validate();

      if (!this.validateVariants()) return;

      if (
        !this.valid ||
        (this.translationsByLanguage && !this.translationsValid)
      ) {
        vueData.addAlert(
          'Asegurese de que todos los datos esten correctos',
          AlertType.Error
        );
        return;
      }

      this.loading = true;
      try {
        // Ensure numeric variant prices before they travel in the item body
        if (this.priceVariants && !this.priceVariants.deletedAt) {
          for (const variant of this.priceVariantItems) {
            variant.cost = Number(variant.cost);
            if (variant.discountedCost !== null)
              variant.discountedCost = Number(variant.discountedCost);
          }
        }

        let hasOptions =
          this.itemOptions?.some((io) => !io.deleted && io.active) ?? false;

        if (this.priceVariants) {
          if (!this.priceVariants.deletedAt && this.priceVariants.active) {
            hasOptions = true;
            const firstVariant = this.priceVariantItems[0];
            this.nItem.discountedPrice = firstVariant
              ? firstVariant.discountedCost
              : null;
          }
        }

        this.nItem.hasOptions = hasOptions;

        let item = !this.nItem.id
          ? await this.itemsService.create(this.nItem)
          : await this.itemsService.update(this.nItem);
        if (!this.nItem.id) {
          this.nItem.id = item.id;
          this.$gtag.event('create', { event_category: 'item' });
        }

        if (this.translationsChanged) {
          const translations = [];
          for (const langId in this.translationsByLanguage) {
            if (this.translationsByLanguage[langId].id) continue;
            translations.push(this.translationsByLanguage[langId]);
          }
          await this.translationsService.saveItemTranslations(
            item.restaurantId,
            item.id,
            translations
          );
        }

        await this.saveVariantTranslations();

        await this.saveItemOptions(item);

        if (this.$refs.childStockRecipes) {
          await this.$refs.childStockRecipes.handleSave(item);
        }

        vueData.addAlert(this.$tc('item.saved'), AlertType.Success);

        this.$parent.$emit('saved', item);
        this.$emit('close');
      } catch (e) {
        console.error(e);
        vueData.serverError(e, this.$tc('item.errorSaving'));
      } finally {
        this.loading = false;
      }
    }

    /** Creates pending drafts via POST /options, then syncs the links. */
    private async saveItemOptions(item: IItem) {
      if (!this.itemOptions) return;

      const draftRows = this.itemOptions.filter(
        (row) => row.isDraft && !row.deleted
      );
      await Promise.all(
        draftRows.map(async (row) => {
          const created = await OptionsService.create({
            ...this.toCreateOptionDto(row.option),
            restaurantId: item.restaurantId,
            itemId: item.id,
            menuId: this.menuId ?? null,
          });
          Vue.set(row, 'option', created);
          Vue.set(row, 'isDraft', false);
        })
      );

      this.itemOptions.forEach((row, idx) => {
        Vue.set(row, 'index', idx);
      });

      const links = this.itemOptions
        .filter((row) => row.option.id)
        .map((row) => ({
          optionId: row.option.id,
          active: row.active,
          index: row.index,
          deleted: !!row.deleted,
        }));

      if (links.length > 0) await this.itemsService.saveOptions(item, links);
    }

    round(val) {
      return roundPrice(val);
    }

    cancel() {
      this.$emit('close');
    }

    async loadCategories() {
      const categories = await RestaurantsService.getCategories(
        this.item.restaurantId,
        this.menuId
      );
      this.categories = categories;
    }

    async loadReportingCategories() {
      try {
        this.reportingCategories = await ReportingCategoriesService.getAll(
          this.item.restaurantId
        );
      } catch (e) {
        // The override field simply stays hidden.
        console.error('Error loading reporting categories', e);
      }
    }

    /** Leaf reporting categories (parents with children are ambiguous). */
    get reportingCategoryOptions() {
      const all = this.reportingCategories;
      const byId = new Map(all.map((row) => [row.id, row]));
      const leaves = all
        .filter((row) => !all.some((other) => other.parentId === row.id))
        .map((row) => ({
          value: row.id,
          text: row.parentId
            ? `${byId.get(row.parentId)?.name ?? ''} › ${row.name}`
            : row.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
      return [
        {
          value: null,
          text: this.$t('reportingCategories.inheritOption') as string,
        },
        ...leaves,
      ];
    }

    async remove() {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t('delete.title', { name: this.nItem.name }),
          message: this.$t('delete.warning'),
        },
        {
          name: 'delete',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          confirm: async () => {
            try {
              await this.itemsService.delete(this.item);
              this.$modal.hide('delete');
              this.$parent.$emit('deleted');
            } catch (e) {
              console.error(e);
              vueData.serverError(e);
            }
          },
        }
      );
    }

  }
</script>

<style lang="scss" scoped>
  .branches {
    padding: 10px;
    border-radius: 10px;
    background-color: #fafafa;

    .v-input:first-child {
      margin-top: 0;
    }
  }
</style>
